import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { cn } from '@/shared/lib/utils';

type CalendarView = 'day' | 'month' | 'year';

interface CalendarPickerProps {
  value: Date | null;
  onChange: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  'data-testid'?: string;
  /** Which day starts the week. Defaults to `'monday'` (matches Rails `SYSTEM_DEFAULT_VALUES.start_dow`). */
  startDow?: 'sunday' | 'monday';
}

// ─── NavButton must live OUTSIDE the CalendarPickerBase function so that its
// component identity is stable across renders. If it were defined inside, React
// would see a *new* component type on every render and unmount/remount the
// button DOM nodes. That causes the mousedown target to be detached from the
// portal by the time DatePickerWithCalendar's handleClickOutside runs its
// `contains()` check, making the calendar close immediately when any nav arrow
// is clicked.
interface NavButtonProps {
  direction: 'left' | 'right';
  onClick: () => void;
  ariaLabel: string;
  testId: string;
  disabled?: boolean;
}

const NavButton: React.FC<NavButtonProps> = ({ direction, onClick, ariaLabel, testId, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'flex items-center justify-center w-7 h-7 rounded-md',
      'hover:bg-surface-alt transition-colors',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      'focus:outline-none focus:ring-2 focus:ring-primary',
    )}
    aria-label={ariaLabel}
    data-testid={testId}
  >
    <svg className="w-4 h-4 text-text" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d={direction === 'left' ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'}
      />
    </svg>
  </button>
);

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const MONTHS_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Lifted to module scope so a new array is not allocated on every render.
const BASE_DAYS_OF_WEEK = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const BASE_DAYS_OF_WEEK_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Number of years shown per page in the year-picker view. */
const YEAR_PAGE_SIZE = 12;

/** Format a Date to a full accessible label, e.g. "June 10, 2026" */
const formatFullDate = (date: Date): string =>
  new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(date);

/** Check if two dates are the same calendar day */
const isSameDay = (a: Date, b: Date): boolean =>
  a.getDate() === b.getDate() &&
  a.getMonth() === b.getMonth() &&
  a.getFullYear() === b.getFullYear();

const CalendarPickerBase: React.FC<CalendarPickerProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  disabled = false,
  'data-testid': dataTestId,
  // Default matches Rails SYSTEM_DEFAULT_VALUES.start_dow = 'monday'
  // (view_preference.rb mf_start_dow_monday? treats absent/nil as monday).
  startDow = 'monday',
}) => {
  const [currentMonth, setCurrentMonth] = useState<Date>(value || new Date());
  const [view, setView] = useState<CalendarView>('day');

  // Year-grid page offset (multiples of YEAR_PAGE_SIZE relative to currentMonth's year)
  const [yearPageOffset, setYearPageOffset] = useState<number>(0);

  // Tracks the currently keyboard-focused date within the day grid (roving tabIndex)
  const [focusedDate, setFocusedDate] = useState<Date>(value || new Date());

  // Tracks the currently keyboard-focused month index in month view
  const [focusedMonth, setFocusedMonth] = useState<number>((value || new Date()).getMonth());

  // Tracks the currently keyboard-focused year in year view
  const [focusedYear, setFocusedYear] = useState<number>((value || new Date()).getFullYear());

  // Ref to the day grid container — used to imperatively focus individual cells
  const gridRef = useRef<HTMLDivElement>(null);

  // Sync focused-date/month/year when the controlled `value` prop changes from
  // the parent (e.g. user clears the field or an external form sets a new date).
  // Without this, the roving-tabIndex marker stays on the old date even though
  // the selected date visually updates. §4.1.2 — name/role/value must stay in sync.
  useEffect(() => {
    if (value) {
      setFocusedDate(value);
      setFocusedMonth(value.getMonth());
      setFocusedYear(value.getFullYear());
    }
  }, [value]);

  // 0 = Sunday-first, 1 = Monday-first
  const weekStartDay: 0 | 1 = startDow === 'monday' ? 1 : 0;

  // Build the day-header row starting from weekStartDay
  const daysOfWeek = [
    ...BASE_DAYS_OF_WEEK.slice(weekStartDay),
    ...BASE_DAYS_OF_WEEK.slice(0, weekStartDay),
  ];

  const daysOfWeekFull = [
    ...BASE_DAYS_OF_WEEK_FULL.slice(weekStartDay),
    ...BASE_DAYS_OF_WEEK_FULL.slice(0, weekStartDay),
  ];

  // ─── Derived display strings ────────────────────────────────────────────────

  const monthYear = useMemo(() => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      year: 'numeric',
    }).format(currentMonth);
  }, [currentMonth]);

  const currentYear = currentMonth.getFullYear();

  // First year shown on the current year-page
  const yearPageStart = useMemo(
    () => currentYear + yearPageOffset,
    [currentYear, yearPageOffset],
  );

  const yearGrid = useMemo(
    () => Array.from({ length: YEAR_PAGE_SIZE }, (_, i) => yearPageStart + i),
    [yearPageStart],
  );

  // ─── Day-view helpers ───────────────────────────────────────────────────────

  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Roll back to the nearest weekStartDay on or before the first of the month.
    const startDate = new Date(firstDay);
    const startOffset = (firstDay.getDay() - weekStartDay + 7) % 7;
    startDate.setDate(startDate.getDate() - startOffset);

    // Roll forward to the last column of the grid row.
    const endDate = new Date(lastDay);
    const lastDow = (weekStartDay - 1 + 7) % 7;
    const endOffset = (lastDow - lastDay.getDay() + 7) % 7;
    endDate.setDate(endDate.getDate() + endOffset);

    const days: Date[] = [];
    const current = new Date(startDate);

    while (current <= endDate) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  }, [currentMonth, weekStartDay]);

  const isDateDisabled = (date: Date): boolean => {
    if (disabled) return true;
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  const isCurrentMonth = (date: Date): boolean =>
    date.getMonth() === currentMonth.getMonth();

  const isSelected = (date: Date): boolean => {
    if (!value) return false;
    return isSameDay(date, value);
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return isSameDay(date, today);
  };

  // ─── Month-view helpers ─────────────────────────────────────────────────────

  const isMonthDisabled = (monthIdx: number): boolean => {
    if (disabled) return true;
    const year = currentMonth.getFullYear();
    const lastDayOfMonth = new Date(year, monthIdx + 1, 0);
    const firstDayOfMonth = new Date(year, monthIdx, 1);
    if (minDate && lastDayOfMonth < minDate) return true;
    if (maxDate && firstDayOfMonth > maxDate) return true;
    return false;
  };

  const isSelectedMonth = (monthIdx: number): boolean => {
    if (!value) return false;
    return value.getMonth() === monthIdx && value.getFullYear() === currentMonth.getFullYear();
  };

  const isCurrentMonthHighlight = (monthIdx: number): boolean => {
    const today = new Date();
    return monthIdx === today.getMonth() && currentMonth.getFullYear() === today.getFullYear();
  };

  // ─── Year-view helpers ──────────────────────────────────────────────────────

  const isYearDisabled = (year: number): boolean => {
    if (disabled) return true;
    const lastDayOfYear = new Date(year, 11, 31);
    const firstDayOfYear = new Date(year, 0, 1);
    if (minDate && lastDayOfYear < minDate) return true;
    if (maxDate && firstDayOfYear > maxDate) return true;
    return false;
  };

  const isSelectedYear = (year: number): boolean => {
    if (!value) return false;
    return value.getFullYear() === year;
  };

  const isCurrentYear = (year: number): boolean => new Date().getFullYear() === year;

  // ─── Navigation handlers ────────────────────────────────────────────────────

  const handlePreviousMonth = (): void => {
    setCurrentMonth((prev) => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() - 1);
      return d;
    });
  };

  const handleNextMonth = (): void => {
    setCurrentMonth((prev) => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + 1);
      return d;
    });
  };

  const handlePreviousYear = (): void => {
    setCurrentMonth((prev) => {
      const d = new Date(prev);
      d.setFullYear(d.getFullYear() - 1);
      return d;
    });
  };

  const handleNextYear = (): void => {
    setCurrentMonth((prev) => {
      const d = new Date(prev);
      d.setFullYear(d.getFullYear() + 1);
      return d;
    });
  };

  const handleDateClick = (date: Date): void => {
    if (!isDateDisabled(date)) {
      onChange(date);
      setFocusedDate(date);
    }
  };

  const handleMonthSelect = (monthIdx: number): void => {
    if (isMonthDisabled(monthIdx)) return;
    setCurrentMonth((prev) => {
      const d = new Date(prev);
      d.setMonth(monthIdx);
      return d;
    });
    setFocusedMonth(monthIdx);
    setView('day');
  };

  const handleYearSelect = (year: number): void => {
    if (isYearDisabled(year)) return;
    setCurrentMonth((prev) => {
      const d = new Date(prev);
      d.setFullYear(year);
      return d;
    });
    setFocusedYear(year);
    setYearPageOffset(0);
    setView('month');
  };

  const handleHeaderClick = (): void => {
    if (disabled) return;
    if (view === 'day') {
      setView('month');
    } else if (view === 'month') {
      setYearPageOffset(0);
      setView('year');
    }
    // year view header click does nothing (already at deepest)
  };

  // ─── Roving tabIndex focus helper ───────────────────────────────────────────

  // After state updates, imperatively focus the button for `focusedDate` within the grid.
  const focusDateButton = useCallback((date: Date) => {
    if (!gridRef.current) return;
    const buttons = gridRef.current.querySelectorAll<HTMLButtonElement>('button[data-date]');
    buttons.forEach((btn) => {
      const btnDate = btn.getAttribute('data-date');
      if (btnDate) {
        const d = new Date(btnDate);
        if (isSameDay(d, date)) {
          btn.focus();
        }
      }
    });
  }, []);

  // ─── Day-grid keyboard navigation ───────────────────────────────────────────

  const handleDayKeyDown = useCallback((e: React.KeyboardEvent<HTMLButtonElement>, date: Date): void => {
    let newDate: Date | null = null;

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        newDate = new Date(date);
        newDate.setDate(newDate.getDate() - 1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        newDate = new Date(date);
        newDate.setDate(newDate.getDate() + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        newDate = new Date(date);
        newDate.setDate(newDate.getDate() - 7);
        break;
      case 'ArrowDown':
        e.preventDefault();
        newDate = new Date(date);
        newDate.setDate(newDate.getDate() + 7);
        break;
      case 'Home':
        // Move to first day of current week row
        e.preventDefault();
        newDate = new Date(date);
        {
          const dayOfWeek = (date.getDay() - weekStartDay + 7) % 7;
          newDate.setDate(newDate.getDate() - dayOfWeek);
        }
        break;
      case 'End':
        // Move to last day of current week row
        e.preventDefault();
        newDate = new Date(date);
        {
          const dayOfWeek = (date.getDay() - weekStartDay + 7) % 7;
          newDate.setDate(newDate.getDate() + (6 - dayOfWeek));
        }
        break;
      case 'PageUp':
        // Move to same day in previous month
        e.preventDefault();
        newDate = new Date(date);
        newDate.setMonth(newDate.getMonth() - 1);
        break;
      case 'PageDown':
        // Move to same day in next month
        e.preventDefault();
        newDate = new Date(date);
        newDate.setMonth(newDate.getMonth() + 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!isDateDisabled(date)) {
          onChange(date);
          setFocusedDate(date);
        }
        return;
      default:
        return;
    }

    if (newDate) {
      setFocusedDate(newDate);
      // If the new date is outside current month, navigate the calendar
      if (
        newDate.getMonth() !== currentMonth.getMonth() ||
        newDate.getFullYear() !== currentMonth.getFullYear()
      ) {
        setCurrentMonth(new Date(newDate.getFullYear(), newDate.getMonth(), 1));
      }
    }
  }, [currentMonth, weekStartDay, isDateDisabled, onChange]);

  // After focusedDate changes, imperatively move focus to the correct button
  useEffect(() => {
    if (view === 'day') {
      focusDateButton(focusedDate);
    }
  }, [focusedDate, view, focusDateButton, calendarDays]);

  // ─── Month-grid keyboard navigation ─────────────────────────────────────────

  const focusMonthButton = useCallback((monthIdx: number) => {
    if (!gridRef.current) return;
    const btn = gridRef.current.querySelector<HTMLButtonElement>(`button[data-month="${monthIdx}"]`);
    btn?.focus();
  }, []);

  const handleMonthKeyDown = useCallback((e: React.KeyboardEvent<HTMLButtonElement>, monthIdx: number): void => {
    let newMonth: number | null = null;

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        newMonth = monthIdx - 1;
        if (newMonth < 0) {
          newMonth = 11;
          handlePreviousYear();
        }
        break;
      case 'ArrowRight':
        e.preventDefault();
        newMonth = monthIdx + 1;
        if (newMonth > 11) {
          newMonth = 0;
          handleNextYear();
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        newMonth = monthIdx - 3;
        if (newMonth < 0) {
          newMonth += 12;
          handlePreviousYear();
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        newMonth = monthIdx + 3;
        if (newMonth > 11) {
          newMonth -= 12;
          handleNextYear();
        }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleMonthSelect(monthIdx);
        return;
      default:
        return;
    }

    if (newMonth !== null) {
      setFocusedMonth(newMonth);
      focusMonthButton(newMonth);
    }
  }, [handlePreviousYear, handleNextYear, handleMonthSelect, focusMonthButton]);

  // ─── Year-grid keyboard navigation ──────────────────────────────────────────

  const focusYearButton = useCallback((year: number) => {
    if (!gridRef.current) return;
    const btn = gridRef.current.querySelector<HTMLButtonElement>(`button[data-year="${year}"]`);
    btn?.focus();
  }, []);

  const handleYearKeyDown = useCallback((e: React.KeyboardEvent<HTMLButtonElement>, year: number): void => {
    let newYear: number | null = null;

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        newYear = year - 1;
        if (newYear < yearPageStart) {
          setYearPageOffset((o) => o - YEAR_PAGE_SIZE);
        }
        break;
      case 'ArrowRight':
        e.preventDefault();
        newYear = year + 1;
        if (newYear >= yearPageStart + YEAR_PAGE_SIZE) {
          setYearPageOffset((o) => o + YEAR_PAGE_SIZE);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        newYear = year - 3;
        if (newYear < yearPageStart) {
          setYearPageOffset((o) => o - YEAR_PAGE_SIZE);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        newYear = year + 3;
        if (newYear >= yearPageStart + YEAR_PAGE_SIZE) {
          setYearPageOffset((o) => o + YEAR_PAGE_SIZE);
        }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleYearSelect(year);
        return;
      default:
        return;
    }

    if (newYear !== null) {
      setFocusedYear(newYear);
      focusYearButton(newYear);
    }
  }, [yearPageStart, handleYearSelect, focusYearButton]);

  // When year page changes (pagination), re-focus the target year button
  useEffect(() => {
    if (view === 'year') {
      focusYearButton(focusedYear);
    }
  }, [yearPageOffset, view, focusedYear, focusYearButton]);

  // When month view shows after year navigation, re-focus the target month button
  useEffect(() => {
    if (view === 'month') {
      focusMonthButton(focusedMonth);
    }
  }, [currentYear, view, focusedMonth, focusMonthButton]);

  // ─── Build week rows for the day grid ───────────────────────────────────────

  const weekRows = useMemo(() => {
    const rows: Date[][] = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      rows.push(calendarDays.slice(i, i + 7));
    }
    return rows;
  }, [calendarDays]);

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
    role="application"
      className="bg-surface rounded-lg shadow-lg p-3 w-[280px]"
      data-testid={dataTestId || 'calendar-picker'}
    >
      {/* Screen-reader live region — announces month/year as user navigates */}
      <div aria-live="polite" aria-atomic="true" className="sr-only" role="status">
        {view === 'day' && monthYear}
        {view === 'month' && `${currentYear}`}
        {view === 'year' && `${yearPageStart} to ${yearPageStart + YEAR_PAGE_SIZE - 1}`}
      </div>

      {/* ── DAY VIEW ─────────────────────────────────────────────────────── */}
      {view === 'day' && (
        <>
          <div className="flex items-center justify-between mb-3">
            <NavButton
              direction="left"
              onClick={handlePreviousMonth}
              ariaLabel="Previous month"
              testId="calendar-picker-prev-month"
              disabled={disabled}
            />
            <button
              type="button"
              onClick={handleHeaderClick}
              disabled={disabled}
              className={cn(
                'text-sm font-semibold text-text px-2 py-1 rounded-md',
                'hover:bg-surface-alt transition-colors',
                'disabled:cursor-not-allowed disabled:opacity-50',
                'focus:outline-none focus:ring-2 focus:ring-primary',
              )}
              aria-label={`${monthYear}, click to select month`}
              aria-live="polite"
              data-testid="calendar-picker-month-year"
            >
              {monthYear}
            </button>
            <NavButton
              direction="right"
              onClick={handleNextMonth}
              ariaLabel="Next month"
              testId="calendar-picker-next-month"
              disabled={disabled}
            />
          </div>

          {/* Day-of-week column headers */}
          <div
            role="grid"
            aria-label={`${monthYear} calendar`}
            ref={gridRef}
          >
            <div role="row" className="grid grid-cols-7 gap-1 mb-1">
              {daysOfWeek.map((day, i) => (
                <div
                  key={day}
                  role="columnheader"
                  aria-label={daysOfWeekFull[i]}
                  className="text-center text-xs font-medium text-text-muted h-7 flex items-center justify-center"
                >
                  <abbr title={daysOfWeekFull[i]} className="no-underline">{day}</abbr>
                </div>
              ))}
            </div>

            {/* Week rows */}
            {weekRows.map((week, rowIdx) => (
              <div key={rowIdx} role="row" className="grid grid-cols-7 gap-1">
                {week.map((date, colIdx) => {
                  const dateDisabled = isDateDisabled(date);
                  const selected = isSelected(date);
                  const inCurrentMonth = isCurrentMonth(date);
                  const today = isToday(date);
                  const isFocused = isSameDay(date, focusedDate);

                  return (
                    // aria-selected belongs on the gridcell, not on the inner button
                    // (ARIA grid pattern: gridcell conveys selection state)
                    <div key={colIdx} role="gridcell" aria-selected={selected}>
                      <button
                        type="button"
                        onClick={() => handleDateClick(date)}
                        onKeyDown={(e) => handleDayKeyDown(e, date)}
                        onFocus={() => setFocusedDate(date)}
                        disabled={dateDisabled}
                        // Roving tabIndex: only the focused date is reachable via Tab
                        tabIndex={isFocused ? 0 : -1}
                        className={cn(
                          'h-8 w-full rounded-md text-xs font-normal transition-colors',
                          'flex items-center justify-center',
                          'hover:bg-surface-alt',
                          inCurrentMonth && 'text-text',
                          !inCurrentMonth && 'text-text-muted',
                          selected && 'bg-primary/10 text-primary font-medium hover:bg-primary/20',
                          today && !selected && 'border border-primary',
                          dateDisabled && 'cursor-not-allowed opacity-40 hover:bg-transparent',
                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
                        )}
                        aria-label={formatFullDate(date)}
                        data-date={date.toISOString()}
                        data-testid={`calendar-picker-date-${date.getDate()}`}
                      >
                        {date.getDate()}
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── MONTH VIEW ───────────────────────────────────────────────────── */}
      {view === 'month' && (
        <>
          <div className="flex items-center justify-between mb-3">
            <NavButton
              direction="left"
              onClick={handlePreviousYear}
              ariaLabel="Previous year"
              testId="calendar-picker-prev-year"
              disabled={disabled}
            />
            <button
              type="button"
              onClick={handleHeaderClick}
              disabled={disabled}
              className={cn(
                'text-sm font-semibold text-text px-2 py-1 rounded-md',
                'hover:bg-surface-alt transition-colors',
                'disabled:cursor-not-allowed disabled:opacity-50',
                'focus:outline-none focus:ring-2 focus:ring-primary',
              )}
              aria-label={`${currentYear}, click to select year`}
              data-testid="calendar-picker-year-header"
            >
              {currentYear}
            </button>
            <NavButton
              direction="right"
              onClick={handleNextYear}
              ariaLabel="Next year"
              testId="calendar-picker-next-year"
              disabled={disabled}
            />
          </div>

          <div
            role="grid"
            aria-label={`Select month for ${currentYear}`}
            ref={gridRef}
            className="grid grid-cols-3 gap-2"
          >
            {MONTHS.map((name, idx) => {
              const monthDisabled = isMonthDisabled(idx);
              const selected = isSelectedMonth(idx);
              const current = isCurrentMonthHighlight(idx);
              const isFocused = focusedMonth === idx;

              return (
                <div key={name} role="gridcell" aria-selected={selected}>
                  <button
                    type="button"
                    onClick={() => handleMonthSelect(idx)}
                    onKeyDown={(e) => handleMonthKeyDown(e, idx)}
                    onFocus={() => setFocusedMonth(idx)}
                    disabled={monthDisabled}
                    tabIndex={isFocused ? 0 : -1}
                    className={cn(
                      'h-9 w-full rounded-md text-xs font-medium transition-colors',
                      'flex items-center justify-center',
                      'hover:bg-surface-alt',
                      'text-text',
                      selected && 'bg-primary/10 text-primary hover:bg-primary/20',
                      current && !selected && 'border border-primary',
                      monthDisabled && 'cursor-not-allowed opacity-40 hover:bg-transparent',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
                    )}
                    aria-label={MONTHS_FULL[idx]}
                    data-month={idx}
                    data-testid={`calendar-picker-month-${idx}`}
                  >
                    {name}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── YEAR VIEW ────────────────────────────────────────────────────── */}
      {view === 'year' && (
        <>
          <div className="flex items-center justify-between mb-3">
            <NavButton
              direction="left"
              onClick={() => setYearPageOffset((o) => o - YEAR_PAGE_SIZE)}
              ariaLabel="Previous years"
              testId="calendar-picker-prev-years"
              disabled={disabled}
            />
            <span
              className="text-sm font-semibold text-text"
              data-testid="calendar-picker-year-range"
              aria-hidden="true"
            >
              {yearPageStart}–{yearPageStart + YEAR_PAGE_SIZE - 1}
            </span>
            <NavButton
              direction="right"
              onClick={() => setYearPageOffset((o) => o + YEAR_PAGE_SIZE)}
              ariaLabel="Next years"
              testId="calendar-picker-next-years"
              disabled={disabled}
            />
          </div>

          <div
            role="grid"
            aria-label={`Select year, showing ${yearPageStart} to ${yearPageStart + YEAR_PAGE_SIZE - 1}`}
            ref={gridRef}
            className="grid grid-cols-3 gap-2"
          >
            {yearGrid.map((year) => {
              const yearDisabled = isYearDisabled(year);
              const selected = isSelectedYear(year);
              const current = isCurrentYear(year);
              const isFocused = focusedYear === year;

              return (
                <div key={year} role="gridcell" aria-selected={selected}>
                  <button
                    type="button"
                    onClick={() => handleYearSelect(year)}
                    onKeyDown={(e) => handleYearKeyDown(e, year)}
                    onFocus={() => setFocusedYear(year)}
                    disabled={yearDisabled}
                    tabIndex={isFocused ? 0 : -1}
                    className={cn(
                      'h-9 w-full rounded-md text-xs font-medium transition-colors',
                      'flex items-center justify-center',
                      'hover:bg-surface-alt',
                      'text-text',
                      selected && 'bg-primary/10 text-primary hover:bg-primary/20',
                      current && !selected && 'border border-primary',
                      yearDisabled && 'cursor-not-allowed opacity-40 hover:bg-transparent',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
                    )}
                    aria-label={String(year)}
                    data-year={year}
                    data-testid={`calendar-picker-year-${year}`}
                  >
                    {year}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export const CalendarPicker = React.memo(CalendarPickerBase);
